/** SUNTRONIX ONE — Public, unauthenticated ONE Forms submission page (/f/:token).
 *  Renders a published form shared via a public link; external respondents can
 *  fill and submit. Creates a governed request server-side (source=public). */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/lib/api";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Loader2, AlertTriangle, Send } from "lucide-react";

const isVisible = (f, values) => {
  const c = f.conditional;
  if (!c) return true;
  return String(values[c.field]) === String(c.equals);
};

export default function PublicOneForm() {
  const { token } = useParams();
  const [form, setForm] = useState(null);
  const [err, setErr] = useState(null);
  const [values, setValues] = useState({});
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/one-forms/${token}`)
      .then(({ data }) => setForm(data.form))
      .catch((e) => setErr(e.response?.data?.detail || "This form is not available"));
  }, [token]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/one-forms/${token}/submit`, {
        values, respondent_name: name, respondent_email: email,
      });
      setDone(data);
    } catch (e) {
      setErr(null);
      alert(e.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  const renderField = (f) => {
    if (!isVisible(f, values)) return null;
    const v = values[f.key] ?? "";
    const set = (val) => setValues((s) => ({ ...s, [f.key]: val }));
    const common = { "data-testid": `pf-field-${f.key}`, value: v, onChange: (e) => set(e.target.value) };
    return (
      <div key={f.key} className="space-y-1.5">
        <label className="text-[13px] font-medium text-slate-700">
          {f.label}{f.required && <span className="text-rose-500"> *</span>}
        </label>
        {f.type === "long_text" ? <Textarea {...common} className="text-sm" />
          : f.type === "yesno" ? (
            <div className="flex gap-2">
              {["Yes", "No"].map((o) => (
                <button key={o} type="button" onClick={() => set(o)}
                  className={"px-4 py-1.5 rounded-md text-[13px] border " + (v === o ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200")}>{o}</button>
              ))}
            </div>)
          : ["dropdown", "radio"].includes(f.type) && f.options ? (
            <select {...common} className="w-full h-10 rounded-md border border-slate-200 text-sm px-2">
              <option value="">Select…</option>
              {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>)
          : f.type === "multiselect" && f.options ? (
            <div className="flex flex-wrap gap-1.5">
              {f.options.map((o) => {
                const arr = Array.isArray(v) ? v : [];
                const on = arr.includes(o);
                return <button key={o} type="button" onClick={() => set(on ? arr.filter((x) => x !== o) : [...arr, o])}
                  className={"px-3 py-1.5 rounded-full text-[12px] border " + (on ? "bg-sky-600 text-white border-sky-600" : "bg-white border-slate-200")}>{o}</button>;
              })}
            </div>)
          : <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "email" ? "email" : "text"} {...common} className="h-10 text-sm" />}
      </div>
    );
  };

  if (err) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="pf-error">
      <div className="text-center max-w-sm">
        <AlertTriangle className="h-10 w-10 mx-auto mb-3 text-amber-500" />
        <p className="text-slate-700 font-medium">{err}</p>
      </div>
    </div>
  );

  if (done) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="pf-done">
      <div className="text-center max-w-sm bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
        <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
        <h1 className="text-lg font-semibold text-slate-900">Thank you</h1>
        <p className="text-sm text-slate-500 mt-1">Your response has been received.</p>
        <div className="mt-4 inline-block text-[12px] bg-slate-100 text-slate-600 rounded-md px-3 py-1.5">
          Reference <span className="font-semibold text-slate-800">{done.request_no}</span>
        </div>
      </div>
    </div>
  );

  if (!form) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="public-one-form">
      <div className="max-w-2xl mx-auto space-y-5">
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold tracking-tight text-slate-900">SUNTRONIX</span>
          <span className="text-[11px] uppercase tracking-widest text-slate-400">ONE</span>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-1">
          <h1 className="text-xl font-semibold text-slate-900">{form.name}</h1>
          {form.purpose && <p className="text-sm text-slate-500">{form.purpose}</p>}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-3">
          <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-400">Your details</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-[13px] font-medium text-slate-700">Your name</label>
              <Input data-testid="pf-respondent-name" value={name} onChange={(e) => setName(e.target.value)} className="h-10 text-sm" />
            </div>
            <div className="space-y-1.5">
              <label className="text-[13px] font-medium text-slate-700">Your email</label>
              <Input data-testid="pf-respondent-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-10 text-sm" />
            </div>
          </div>
        </div>

        {(form.sections || []).map((sec, i) => (
          <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
            <div className="text-[13px] font-semibold text-slate-800">{sec.title}</div>
            {(sec.fields || []).map(renderField)}
          </div>
        ))}

        <Button onClick={submit} disabled={busy} data-testid="pf-submit" className="w-full h-11 bg-sky-600 hover:bg-sky-700 text-white">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Submit
        </Button>
        <p className="text-center text-[11px] text-slate-400">Powered by SUNTRONIX ONE · ONE Forms</p>
      </div>
    </div>
  );
}
